export const ONPREM_DESKTOP_RECOVERY_PATH = '/onprem/recover';

export type OnPremDesktopRecoveryReason =
  | 'logout'
  | 'auth-error'
  | 'callback-error'
  | 'error-boundary';

const OIDC_STORAGE_PREFIXES = ['oidc.', 'oidc.user:'];
// UNCARVED DOMAIN KEY (onprem, MOD-1478): onprem has no workspace package, so
// there is no /contracts door to declare this in. Not a query cache identity —
// a cleanup list of existing browser-storage keys the desktop recovery flow
// removes; never read into a queryKey/mutationKey head.
const RECOVERY_LOCAL_STORAGE_KEYS = [
  'REACT_QUERY_OFFLINE_CACHE',
  'heidi-sidebar-expanded',
];

function getStorage(readStorage: () => Storage): Storage | undefined {
  try {
    return readStorage();
  } catch {
    return undefined;
  }
}

function removeStorageKeys(
  storage: Storage | undefined,
  shouldRemove: (key: string) => boolean
): void {
  if (!storage) return;

  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && shouldRemove(key)) keys.push(key);
  }

  keys.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // best-effort recovery cleanup
    }
  });
}

function isOidcStorageKey(key: string): boolean {
  return OIDC_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export function getOnPremDesktopRecoveryUrl(reason: OnPremDesktopRecoveryReason): string {
  return `${ONPREM_DESKTOP_RECOVERY_PATH}?reason=${encodeURIComponent(reason)}`;
}

export function navigateToOnPremDesktopRecovery(
  reason: OnPremDesktopRecoveryReason
): void {
  if (typeof window === 'undefined') return;

  const target = getOnPremDesktopRecoveryUrl(reason);
  if (typeof window.location.replace === 'function') {
    window.location.replace(target);
    return;
  }
  window.location.assign(target);
}

export function clearOnPremDesktopRecoveryState(): void {
  if (typeof window === 'undefined') return;

  const localStorage = getStorage(() => window.localStorage);
  const sessionStorage = getStorage(() => window.sessionStorage);

  RECOVERY_LOCAL_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage?.removeItem(key);
    } catch {
      // best-effort recovery cleanup
    }
  });

  removeStorageKeys(localStorage, isOidcStorageKey);
  removeStorageKeys(sessionStorage, isOidcStorageKey);
}

export async function prepareOnPremDesktopRelogin(): Promise<void> {
  clearOnPremDesktopRecoveryState();

  try {
    const { clearAuthCache } = await import('@/lib/auth');
    clearAuthCache({ reason: 'logout' });
  } catch {
    // best-effort recovery cleanup
  }

  try {
    const { clearKeycloakTauriSession } = await import('@/tauri/keycloak/oidcClient');
    await clearKeycloakTauriSession({ requireFreshLogin: true });
  } catch {
    // best-effort recovery cleanup
  }

  clearOnPremDesktopRecoveryState();
}
